import { api } from "@/lib/api";

export type RoutineStatus = "active" | "paused" | "archived";

export type RoutineTrigger = {
  id: string;
  kind: "schedule" | "webhook" | "api";
  label: string | null;
  enabled: boolean;
  cronExpression?: string | null;
  timezone?: string | null;
  nextRunAt?: string | null;
  lastFiredAt?: string | null;
  lastResult?: unknown;
};

export type RoutineLastRun = {
  id: string;
  status: string;
  triggeredAt: string;
  completedAt: string | null;
  trigger?: { kind: string; label: string | null } | null;
} | null;

export type Routine = {
  id: string;
  companyId: string;
  projectId: string | null;
  title: string;
  description: string | null;
  status: RoutineStatus;
  protected: boolean;
  /** Ritual em desenvolvimento — UI mostra cadeado, backend bloqueia ativação. */
  comingSoon: boolean;
  assigneeAgentId: string | null;
  priority: string;
  variables: unknown[];
  createdAt: string;
  updatedAt: string;
  triggers: RoutineTrigger[];
  lastRun: RoutineLastRun;
  activeIssue?: { id: string; title: string; status: string } | null;
};

export type RoutineRunSummary = {
  id: string;
  companyId: string;
  routineId: string;
  routineTitle: string;
  source: "schedule" | "webhook" | "api" | "manual";
  /**
   * Status do run:
   *  - queued     → enfileirado, ainda não rodou
   *  - running    → executando agora
   *  - completed  → terminou e gerou issue/entregável
   *  - succeeded  → terminou OK sem criar issue. Em rituais que rodam
   *                 direto-API (ex: "Aprende com seus aprovados"), o
   *                 resultado fica em triggerPayload (agentsAnalyzed,
   *                 costCents, etc) — não é "sem ação", é trabalho real
   *                 que atualiza dados internos sem virar card.
   *  - failed     → erro
   *  - skipped    → pulou por política (concorrência, catch-up)
   *  - coalesced  → consolidado em outro run ativo
   */
  status:
    | "queued"
    | "running"
    | "completed"
    | "succeeded"
    | "failed"
    | "skipped"
    | "coalesced";
  /**
   * Payload livre persistido pelo runner. Usado pra rituais que rodam
   * direto-API e precisam reportar resumo do trabalho:
   *
   *   { kind: "direct_api_learn_from_approved",
   *     agentsAnalyzed: 3, agentsSkipped: 1,
   *     costCents: 63, durationMs: 23000 }
   */
  triggerPayload: Record<string, unknown> | null;
  triggeredAt: string;
  completedAt: string | null;
  failureReason: string | null;
  linkedIssue: {
    id: string;
    identifier: string | null;
    title: string;
    status: string;
    priority: string;
  } | null;
  trigger: {
    id: string;
    kind: "schedule" | "webhook" | "api";
    label: string | null;
  } | null;
};

export const routinesApi = {
  list: (companyId: string) =>
    api.get<Routine[]>(`/companies/${companyId}/routines`),
  patch: (routineId: string, body: { status?: RoutineStatus; title?: string }) =>
    api.patch<Routine>(`/routines/${routineId}`, body),
  runs: (companyId: string, limit = 50) =>
    api.get<RoutineRunSummary[]>(`/companies/${companyId}/routine-runs?limit=${limit}`),
};
